import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  select,
} from '@ngrx/store';
import { auth } from 'firebase';
import { pipe } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { PhoneNumber } from 'src/app/models/auth/phone-number';
import { AllActionsUnion } from '../../../app.actions';
import {
  resetState,
  showNoProfileAlert,
  storeConfirmationResult,
  storeErrorMessage,
  storePhoneNumber,
  updateLoading,
} from './sign-in.actions';

export interface SignInState {
  confirmationResult: auth.ConfirmationResult | null;
  phoneNumber: PhoneNumber;
  showNoProfileAlert: boolean;
  loading: boolean;
  errorMessage: string;
}

const initialState: SignInState = {
  confirmationResult: null,
  phoneNumber: new PhoneNumber(''),
  showNoProfileAlert: false,
  loading: false,
  errorMessage: '',
};

export const featureName = 'signInFeature';

export function signInReducer(
  state_: SignInState | undefined,
  action: AllActionsUnion,
): SignInState {
  const reducer_ = createReducer<SignInState>(
    initialState,
    on(
      storeConfirmationResult,
      (state, { payload }): SignInState => {
        return { ...state, confirmationResult: payload };
      },
    ),
    on(
      storePhoneNumber,
      (state, { payload }): SignInState => {
        return { ...state, phoneNumber: payload };
      },
    ),
    on(
      showNoProfileAlert,
      (state): SignInState => {
        return { ...state, showNoProfileAlert: true };
      },
    ),
    on(
      updateLoading,
      (state, { payload }): SignInState => {
        return { ...state, loading: payload };
      },
    ),
    on(
      storeErrorMessage,
      (state, { payload }): SignInState => {
        return { ...state, errorMessage: payload };
      },
    ),
    on(
      resetState,
      (): SignInState => {
        return { ...initialState };
      },
    ),
  );
  return reducer_(state_, action);
}

const getFeature = createFeatureSelector<SignInState>(featureName);

export const getConfirmationResult = pipe(
  select(
    createSelector(
      getFeature,
      v => v.confirmationResult,
    ),
  ),
);

export const getPhoneNumber = pipe(
  select(
    createSelector(
      getFeature,
      v => v.phoneNumber,
    ),
  ),
  filter(v => v.hasValue()),
);

export const getShowNoProfileAlert = pipe(
  select(
    createSelector(
      getFeature,
      v => v.showNoProfileAlert,
    ),
  ),
);

export const getLoading = pipe(
  select(
    createSelector(
      getFeature,
      v => v.loading,
    ),
  ),
  distinctUntilChanged(),
);

export const getErrorMessage = pipe(
  select(
    createSelector(
      getFeature,
      v => v.errorMessage,
    ),
  ),
  distinctUntilChanged(),
);
